import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Building2, Mail, Lock, User, Check, 
  ChevronRight, Sparkles, Users, FileText, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterClinic() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  
  const [formData, setFormData] = useState({
    nome: '',
    slug: '',
    email_admin: '',
    senha_admin: '',
    confirmar_senha: '',
    plano: 'starter'
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/tenant/plans`);
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleNameChange = (e) => {
    const nome = e.target.value;
    setFormData(prev => ({
      ...prev,
      nome,
      slug: generateSlug(nome)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.senha_admin !== formData.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (formData.senha_admin.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/tenant/register`, {
        nome: formData.nome,
        slug: formData.slug,
        email_admin: formData.email_admin,
        senha_admin: formData.senha_admin,
        plano: formData.plano
      });

      toast.success('Clínica cadastrada com sucesso!');
      
      // Auto-login after registration
      try {
        const loginResponse = await axios.post(`${API}/auth/login`, {
          login: formData.email_admin,
          senha: formData.senha_admin
        });

        localStorage.setItem('token', loginResponse.data.access_token);
        localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
        
        navigate('/');
      } catch (loginError) {
        // If auto-login fails, redirect to login page
        navigate('/login');
      }
    } catch (error) {
      console.error('Erro no cadastro:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar clínica');
    } finally {
      setLoading(false);
    }
  };

  const planFeatures = {
    starter: ['3 usuários', '100 documentos/mês', 'Receitas e pacientes', 'Relatórios básicos'],
    clinica: ['10 usuários', '500 documentos/mês', 'Templates personalizados', 'WhatsApp', 'Backup'],
    prefeitura: ['50 usuários', '5000 documentos/mês', 'Multi-unidades', 'Auditoria completa', 'Suporte prioritário'],
    enterprise: ['Usuários ilimitados', 'Documentos ilimitados', 'API access', 'Suporte dedicado', 'Customizações']
  };

  return (
    <div className="min-h-screen bg-[#F4F7F8] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-[#D1D5DB] w-full max-w-4xl overflow-hidden">
        <div className="grid md:grid-cols-5">
          {/* Left Side - Branding */}
          <div className="md:col-span-2 bg-gradient-to-br from-[#0F766E] to-[#0D6660] p-8 text-white">
            <div className="h-full flex flex-col">
              <div className="mb-8">
                <Building2 className="w-12 h-12 mb-4" />
                <h1 className="text-2xl font-bold">Sistema de Receitas</h1>
                <p className="text-white/80 text-sm mt-2">
                  Crie sua clínica em minutos
                </p>
              </div>
              
              <div className="space-y-4 flex-1">
                <Feature icon={FileText} text="Receitas digitais com QR Code" />
                <Feature icon={Users} text="Gestão completa de pacientes" />
                <Feature icon={Shield} text="Segurança e conformidade LGPD" />
                <Feature icon={Sparkles} text="Templates inteligentes" />
              </div>

              <div className="mt-8 text-sm text-white/60">
                Já tem conta?{' '}
                <button 
                  onClick={() => navigate('/login')}
                  className="text-white underline"
                >
                  Fazer login
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="md:col-span-3 p-8">
            {/* Steps Indicator */}
            <div className="flex items-center gap-2 mb-8">
              <StepIndicator number={1} active={step >= 1} completed={step > 1} label="Plano" />
              <ChevronRight className="w-4 h-4 text-[#D1D5DB]" />
              <StepIndicator number={2} active={step >= 2} completed={step > 2} label="Dados" />
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Plan Selection */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1F2933] mb-1">
                      Escolha seu plano
                    </h2>
                    <p className="text-sm text-[#64748B]">
                      Selecione o plano ideal para sua clínica
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {plans.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, plano: plan.id }))}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.plano === plan.id
                            ? 'border-[#0F766E] bg-[#0F766E]/5'
                            : 'border-[#D1D5DB] hover:border-[#0F766E]/50'
                        }`}
                        data-testid={`plan-${plan.id}`}
                      >
                        <div className="font-semibold text-[#1F2933] mb-2">{plan.name}</div>
                        <ul className="space-y-1">
                          {(planFeatures[plan.id] || []).slice(0, 3).map((feature, idx) => (
                            <li key={idx} className="text-xs text-[#64748B] flex items-center gap-1">
                              <Check className="w-3 h-3 text-[#0F766E]" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </button>
                    ))}
                  </div>

                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full bg-[#0F766E] hover:bg-[#0D6660]"
                    data-testid="next-step-button"
                  >
                    Continuar
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Step 2: Clinic Details */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-[#1F2933] mb-1">
                      Dados da clínica
                    </h2>
                    <p className="text-sm text-[#64748B]">
                      Preencha as informações para criar sua conta
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="nome">Nome da Clínica</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <Input
                          id="nome"
                          value={formData.nome}
                          onChange={handleNameChange}
                          placeholder="Ex: Clínica Saúde Total"
                          className="pl-10"
                          required
                          data-testid="nome-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="slug">URL da sua clínica</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#64748B]">sistemadereceitas.com/</span>
                        <Input
                          id="slug"
                          value={formData.slug}
                          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                          placeholder="minha-clinica"
                          className="flex-1"
                          required
                          data-testid="slug-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email do Administrador</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email_admin}
                          onChange={(e) => setFormData(prev => ({ ...prev, email_admin: e.target.value }))}
                          placeholder="admin@clinica.com"
                          className="pl-10"
                          required
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="senha">Senha</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                          <Input
                            id="senha"
                            type="password"
                            value={formData.senha_admin}
                            onChange={(e) => setFormData(prev => ({ ...prev, senha_admin: e.target.value }))}
                            placeholder="Mínimo 6 caracteres"
                            className="pl-10"
                            required
                            data-testid="senha-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="confirmar_senha">Confirmar Senha</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                          <Input
                            id="confirmar_senha"
                            type="password"
                            value={formData.confirmar_senha}
                            onChange={(e) => setFormData(prev => ({ ...prev, confirmar_senha: e.target.value }))}
                            placeholder="Repita a senha"
                            className="pl-10"
                            required
                            data-testid="confirmar-senha-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1"
                    >
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#0F766E] hover:bg-[#0D6660]"
                      data-testid="register-button"
                    >
                      {loading ? 'Cadastrando...' : 'Criar Clínica'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="bg-white/20 p-2 rounded-lg">
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm">{text}</span>
    </div>
  );
}

function StepIndicator({ number, active, completed, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
        completed 
          ? 'bg-[#0F766E] text-white' 
          : active 
            ? 'bg-[#0F766E]/20 text-[#0F766E] border-2 border-[#0F766E]' 
            : 'bg-[#F4F7F8] text-[#64748B]'
      }`}>
        {completed ? <Check className="w-4 h-4" /> : number}
      </div>
      <span className={`text-sm ${active ? 'text-[#1F2933] font-medium' : 'text-[#64748B]'}`}>
        {label}
      </span>
    </div>
  );
}
